import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Search, Clock3, Users, Lock, Unlock, WifiOff, ShieldCheck, KeyRound,
  CalendarDays, Check, Stethoscope, Menu, X, Sparkles,
} from "lucide-react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Brand, ToothMark } from "@/components/Brand";
import { CompanyInfo, LEGAL_LINKS } from "@/components/Legal";

/* ------------------------------------------------------------------ */
/*  Small building blocks                                              */
/* ------------------------------------------------------------------ */

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`mx-auto w-full max-w-6xl px-5 sm:px-8 ${className}`}>{children}</section>
);

const Heading = ({ children, className = "" }) => (
  <h2 className={`font-head text-3xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-4xl ${className}`}>{children}</h2>
);

const Lede = ({ children, className = "" }) => (
  <p className={`mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-[17px] ${className}`}>{children}</p>
);

/* A window-ish frame that makes product mocks read as "the app" */
const Frame = ({ title, children, className = "" }) => (
  <div className={`dm-panel overflow-hidden bg-white shadow-[0_24px_60px_-28px_rgba(15,23,42,0.35)] ${className}`}>
    <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2.5">
      <ToothMark size={14} />
      <span className="text-xs font-semibold text-slate-600">{title}</span>
    </div>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Product mocks (static, hand-built; no screenshots)                 */
/* ------------------------------------------------------------------ */

const TODAY = [
  { t: "09:30", name: "Meera Kulkarni", proc: "Scaling" },
  { t: "10:15", name: "Arjun Nair", proc: "RCT" },
  { t: "11:00", name: "Fatima Shaikh", proc: "Crown fitting" },
  { t: "11:45", name: "Rohan Deshmukh", proc: "Follow-up" },
];

const HeroMock = () => (
  <div className="relative">
    <Frame title="Today's appointments" className="relative z-10">
      <div className="p-4">
        <div className="dm-input flex items-center gap-2 !py-2 text-sm text-slate-400">
          <Search size={15} /> Search by name, phone or Patient ID…
        </div>
        <ul className="mt-3 divide-y divide-slate-100">
          {TODAY.map((r) => (
            <li key={r.name} className="flex items-center gap-3 py-2.5">
              <span className="font-mono text-xs text-slate-500">{r.t}</span>
              <span className="flex-1 truncate text-sm font-semibold text-slate-800">{r.name}</span>
              <span className="text-xs text-slate-500">{r.proc}</span>
            </li>
          ))}
        </ul>
      </div>
    </Frame>

    {/* Visit entry, offset to show the second half of the workflow */}
    <div className="relative z-20 -mt-6 ml-4 sm:ml-16">
      <Frame title="Log visit — Arjun Nair">
        <div className="p-4">
          <dl className="grid grid-cols-[minmax(72px,88px)_1fr] gap-x-3 gap-y-2 text-sm [&>dd]:min-w-0 [&>dd]:break-words">
            <dt className="text-slate-500">Diagnosis</dt><dd className="font-medium text-slate-800">Irreversible pulpitis, 36</dd>
            <dt className="text-slate-500">Treatment</dt><dd className="font-medium text-slate-800">RCT</dd>
            <dt className="text-slate-500">Consultant</dt><dd className="font-medium text-slate-800">Dr. S. Iyer (Endo)</dd>
            <dt className="text-slate-500">Next visit</dt><dd className="font-medium text-slate-800">Fri, 20 Sep · 10:15</dd>
          </dl>
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
            <span className="flex items-center gap-1.5 text-xs text-slate-500">
              <Clock3 size={13} /> Logged in <span className="font-mono font-semibold text-slate-800">0:17</span>
            </span>
            <span className="dm-btn-primary pointer-events-none !px-3.5 !py-1.5 text-xs">Save visit</span>
          </div>
        </div>
      </Frame>
    </div>
  </div>
);

/* Interactive: the same panel in assistant vs doctor mode */
const DoctorModeDemo = () => {
  const [doctor, setDoctor] = useState(false);
  const rows = [
    ["Revenue collected", "₹1,84,500"],
    ["Consultant fees", "₹42,000"],
    ["Lab costs", "₹18,650"],
    ["Net to clinic", "₹1,23,850"],
  ];
  return (
    <Frame title="Overview — September">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <span className="text-sm text-slate-600">Signed in as <span className="font-semibold text-slate-800">Priya (front desk)</span></span>
        <button
          type="button"
          onClick={() => setDoctor((d) => !d)}
          aria-pressed={doctor}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
            doctor ? "border-teal-600 bg-teal-50 text-teal-700" : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
          }`}
        >
          {doctor ? <Unlock size={13} /> : <Lock size={13} />}
          {doctor ? "Doctor mode on" : "Enter PIN for Doctor mode"}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-px bg-slate-100 sm:grid-cols-4">
        {rows.map(([k, v]) => (
          <div key={k} className="bg-white px-4 py-4">
            <p className="text-xs text-slate-500">{k}</p>
            <p className={`mt-1 font-mono text-lg font-semibold ${doctor ? "text-slate-900" : "text-slate-300"}`} aria-live="polite">
              {doctor ? v : "••••••"}
            </p>
          </div>
        ))}
      </div>
      <p className="px-4 py-3 text-xs text-slate-500">
        {doctor
          ? "Figures are visible only while Doctor mode is on. It locks again when you leave the screen."
          : "Front-desk staff see patients, visits and appointments. Never money."}
      </p>
    </Frame>
  );
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const NAV = [
  ["Features", "#features"],
  ["Doctor mode", "#doctor-mode"],
  ["How it works", "#how-it-works"],
  ["Pricing", "#pricing"],
  ["Clinics", "#clinics"],
];

const PLANS = [
  {
    key: "trial",
    name: "Free Trial",
    price: "₹0",
    priceUnit: "",
    lines: ["30-day free trial", "No credit card required"],
    features: ["Full access to every feature", "Quick search & recall", "Chairside note capture", "Up to 1 dentist"],
    cta: "Start for free",
    emphasize: false,
  },
  {
    key: "annual",
    name: "Annual",
    price: "₹2,999",
    priceUnit: "/year",
    badge: "Best Value",
    lines: ["₹250/month, billed annually", { strike: "₹3,588", save: "Save ₹589/year" }],
    features: ["Unlimited patient memos", "Quick search & recall", "Chairside note capture", "Support included"],
    cta: "Get DentMemo",
    emphasize: true,
    cycle: "annual",
  },
  {
    key: "monthly",
    name: "Monthly",
    price: "₹299",
    priceUnit: "/month",
    lines: ["Billed every month", "Cancel anytime, no lock-in"],
    features: ["Unlimited patient memos", "Quick search & recall", "Chairside note capture", "Support included"],
    cta: "Get DentMemo",
    emphasize: false,
    cycle: "monthly",
  },
];

const STEPS = [
  ["Start your free trial", "Create your DentMemo account."],
  ["Choose your plan", "Monthly or annual. Change anytime."],
  ["Start using DentMemo", "Secure payment via Razorpay."],
];

const FAQS = [
  ["Do I need a card for the trial?", "No. The 30-day trial is completely free and doesn't require a credit card."],
  ["Can I cancel my monthly plan anytime?", "Yes. You can cancel anytime from Settings → Billing, no questions asked."],
  ["Will my annual plan renew?", "Yes, unless you cancel — we'll remind you before renewal."],
  ["What happens after my trial ends?", "You'll be asked to choose a plan before you can keep using DentMemo; your data is safe and waiting."],
];

const PAYMENT_METHODS = [
  { src: "/payment-logos/visa.svg", alt: "Visa" },
  { src: "/payment-logos/mastercard.svg", alt: "Mastercard" },
  { src: "/payment-logos/upi.png", alt: "UPI" },
  { src: "/payment-logos/googlepay.png", alt: "Google Pay" },
  { src: "/payment-logos/paytm.png", alt: "Paytm" },
];

export default function Marketing() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const start = () => nav("/start", { state: { mode: "clinic" } });
  const choosePlan = (cycle) => nav("/start", { state: { mode: "clinic", intent: "subscribe", cycle } });

  return (
    <div className="min-h-screen overflow-x-hidden text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-[#f8fafc]/85 backdrop-blur">
        <Section className="flex h-16 items-center justify-between">
          <Link to="/" aria-label="DentMemo home"><Brand /></Link>
          <nav className="hidden items-center gap-6 whitespace-nowrap text-sm font-semibold text-slate-600 lg:flex">
            {NAV.map(([l, h]) => <a key={h} href={h} className="hover:text-slate-900">{l}</a>)}
          </nav>
          <div className="hidden shrink-0 items-center gap-2 whitespace-nowrap lg:flex">
            <Link to="/login" className="dm-btn-quiet">Log in</Link>
            <button onClick={start} className="dm-btn-primary">Set up your clinic</button>
          </div>
          <button className="dm-btn-quiet !px-2 lg:hidden" aria-label="Menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </Section>
        {open && (
          <div className="border-t border-slate-200 bg-white lg:hidden">
            <Section className="flex flex-col gap-1 py-3">
              {NAV.map(([l, h]) => (
                <a key={h} href={h} onClick={() => setOpen(false)} className="rounded-lg px-2 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">{l}</a>
              ))}
              <div className="mt-2 flex gap-2 border-t border-slate-100 pt-3">
                <Link to="/login" className="dm-btn-ghost flex-1">Log in</Link>
                <button onClick={start} className="dm-btn-primary flex-1">Set up your clinic</button>
              </div>
            </Section>
          </div>
        )}
      </header>

      {/* Hero */}
      <Section className="grid items-center gap-12 pb-20 pt-14 lg:grid-cols-[1fr_1.05fr] lg:gap-16 lg:pb-28 lg:pt-20 [&>*]:min-w-0">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
            <Stethoscope size={13} className="dm-accent-text" /> For dental clinics in India
          </p>
          <h1 className="mt-6 font-head text-[2.6rem] font-extrabold leading-[1.02] tracking-tight text-slate-900 sm:text-6xl">
            The patient register,<br />without the register.
          </h1>
          <p className="mt-6 max-w-lg text-lg leading-relaxed text-slate-600">
            DentMemo is a second brain for your clinic. Find any patient in a second, log a routine visit in under twenty
            seconds, and keep consultant settlements and clinic money straight — even when the internet drops mid-OPD.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={start} className="dm-btn-primary !px-5 !py-3 text-[15px]">
              Set up your clinic <ArrowRight size={16} />
            </button>
            <Link to="/login" className="dm-btn-ghost !px-5 !py-3 text-[15px]">I already have an account</Link>
          </div>
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-500">
            {["Works offline", "DPDP Act consent built in", "Doctor and front-desk roles"].map((t) => (
              <li key={t} className="flex items-center gap-1.5"><Check size={14} className="text-teal-600" /> {t}</li>
            ))}
          </ul>
        </div>
        <HeroMock />
      </Section>

      {/* Why move off paper */}
      <div className="border-y border-slate-200 bg-white/60">
        <Section className="grid gap-8 py-14 md:grid-cols-3 md:gap-12">
          {[
            ["The register you keep today", "A ruled notebook or an Excel sheet. It works until someone asks \"when did Mrs. Kulkarni last come?\" and the front desk starts flipping pages."],
            ["What most software gets wrong", "Hospital-grade systems built for chains: twenty fields per visit, per-seat licences, a training day. Too much for a two-chair clinic."],
            ["What DentMemo does instead", "It keeps the shape of the register — one line per visit — and adds the things paper can't do: search, reminders, settlements, and a lock on the money."],
          ].map(([h, p]) => (
            <div key={h}>
              <h3 className="font-head text-lg font-bold text-slate-900">{h}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{p}</p>
            </div>
          ))}
        </Section>
      </div>

      {/* Features */}
      <Section id="features" className="scroll-mt-20 py-20 lg:py-28">
        <div className="max-w-2xl">
          <Heading>Three things a clinic does every day, done quickly.</Heading>
          <Lede>Everything in DentMemo is built around the front desk's actual rhythm: who is coming, what was done, and who needs to be paid.</Lede>
        </div>

        <div className="mt-14 space-y-16">
          {/* Directory */}
          <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14 [&>*]:min-w-0">
            <div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--dm-accent-soft)] dm-accent-text"><Users size={20} /></div>
              <h3 className="mt-5 font-head text-2xl font-bold text-slate-900">A patient directory that opens on today</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                Today's appointments sit at the top, in order. Below them, every patient you have ever seen — searchable by
                name, phone number or file number, with results as you type. No menus to hunt through between patients.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-600">
                {["Full history on one screen: visits, treatments, pending follow-ups", "Phone number search for walk-ins who forgot their card", "Today's appointments surface first — no scrolling past everyone else"].map((t) => (
                  <li key={t} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0 text-teal-600" />{t}</li>
                ))}
              </ul>
            </div>
            <Frame title="Directory">
              <div className="p-4">
                <div className="dm-input flex items-center gap-2 !py-2 text-sm text-slate-800">
                  <Search size={15} className="text-slate-400" /> kulk<span className="animate-pulse text-slate-400">|</span>
                </div>
                <ul className="mt-3 divide-y divide-slate-100">
                  {[["Meera Kulkarni", "F/42 · #0214", "Last visit 13 Sep · Scaling"], ["Sameer Kulkarni", "M/38 · #0388", "Last visit 2 Aug · Filling, 26"], ["Anita Kulkarni", "F/9 · #0512", "Last visit 15 Jun · Fluoride"]].map(([n, m, l]) => (
                    <li key={n} className="flex items-center gap-3 py-2.5">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-100 text-xs font-bold text-slate-600">{n[0]}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-slate-800">{n}</span>
                        <span className="block truncate text-xs text-slate-500">{l}</span>
                      </span>
                      <span className="font-mono text-xs text-slate-500">{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Frame>
          </div>

          {/* Visit logging */}
          <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14 [&>*]:min-w-0">
            <div className="order-1 lg:order-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--dm-accent-soft)] dm-accent-text"><Clock3 size={20} /></div>
              <h3 className="mt-5 font-head text-2xl font-bold text-slate-900">Log a routine visit in under twenty seconds</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                Four fields: diagnosis, treatment, consultant, next appointment. Common treatments are one tap. The next
                appointment lands on the calendar and in the patient's history the moment you save.
              </p>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                Designed to be filled while the patient is still rinsing, not typed up at the end of the day from memory.
              </p>
            </div>
            <div className="order-2 lg:order-1">
              <Frame title="Log visit">
                <div className="p-4">
                  <p className="label-xs">Treatment</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {["Scaling", "Filling", "RCT", "Extraction", "Crown", "Ortho adj."].map((c, i) => (
                      <span key={c} className={`dm-chip !py-1 text-xs ${i === 2 ? "dm-chip-on" : ""}`}>{c}</span>
                    ))}
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div>
                      <p className="label-xs">Consultant</p>
                      <div className="dm-input mt-1.5 !py-2 text-sm text-slate-800">Dr. S. Iyer</div>
                    </div>
                    <div>
                      <p className="label-xs">Next visit</p>
                      <div className="dm-input mt-1.5 flex items-center gap-2 !py-2 text-sm text-slate-800"><CalendarDays size={14} className="text-slate-400" /> Fri, 20 Sep</div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-mono text-xs text-slate-500">0:00 → 0:17</span>
                    <span className="dm-btn-primary pointer-events-none !px-3.5 !py-1.5 text-xs">Save visit</span>
                  </div>
                </div>
              </Frame>
            </div>
          </div>

          {/* Consultants */}
          <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-14 [&>*]:min-w-0">
            <div>
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--dm-accent-soft)] dm-accent-text"><Stethoscope size={20} /></div>
              <h3 className="mt-5 font-head text-2xl font-bold text-slate-900">Consultant settlements that add themselves up</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">
                Every visit tagged to a visiting specialist becomes a line in their settlement. At month end you see what
                each consultant is owed, what has been paid, and which cases it came from — no reconciling from the register.
              </p>
            </div>
            <Frame title="Consultants — September" className="min-w-0"><div className="overflow-x-auto">
              <table className="dm-table">
                <thead>
                  <tr><th>Consultant</th><th>Cases</th><th className="text-right">Owed</th><th className="text-right">Paid</th></tr>
                </thead>
                <tbody>
                  {[["Dr. S. Iyer", "Endodontics", 9, "₹27,000", "₹18,000"], ["Dr. R. Menon", "Oral surgery", 4, "₹12,000", "₹12,000"], ["Dr. K. Bhat", "Orthodontics", 6, "₹3,000", "—"]].map(([n, s, c, o, p]) => (
                    <tr key={n}>
                      <td><span className="block font-semibold text-slate-800">{n}</span><span className="text-xs text-slate-500">{s}</span></td>
                      <td className="font-mono">{c}</td>
                      <td className="num">{o}</td>
                      <td className={`num ${p === "—" ? "text-slate-400" : ""}`}>{p}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </Frame>
          </div>
        </div>
      </Section>

      {/* Doctor mode */}
      <div id="doctor-mode" className="scroll-mt-20 border-y border-slate-200 bg-white/60">
        <Section className="grid items-center gap-10 py-20 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16 lg:py-28 [&>*]:min-w-0">
          <div>
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700"><Lock size={20} /></div>
            <Heading className="mt-5">Your assistant runs the desk. Only you see the money.</Heading>
            <Lede>
              Revenue, consultant fees and lab costs live behind Doctor mode, opened with a PIN that only the owner knows.
              Front-desk staff use the same app on the same devices and simply never see a rupee figure.
            </Lede>
            <p className="mt-4 text-sm text-slate-500">Try it — the toggle in the demo is live.</p>
          </div>
          <DoctorModeDemo />
        </Section>
      </div>

      {/* Practicalities */}
      <Section className="py-20 lg:py-28">
        <div className="max-w-2xl">
          <Heading>Built for how clinics in India actually run.</Heading>
        </div>
        <div className="mt-12 grid gap-10 md:grid-cols-3 md:gap-8">
          {[
            [WifiOff, "Keeps working when the internet doesn't", "Patients, visits and appointments are cached on the device. Log visits through an outage; everything syncs when the connection returns, with nothing lost or duplicated."],
            [ShieldCheck, "Built with the DPDP Act in mind", "Every clinic account confirms data-handling consent under the Digital Personal Data Protection Act, 2023 at signup. Records can be exported or deleted from Settings at any time. Encrypted in transit and at rest."],
            [KeyRound, "Add staff with a code, not an account plan", "Share a four-digit staff code with your assistant; they sign up and are in your clinic. One flat ₹299/month (or ₹2,999/year) per clinic after a 30-day free trial — no per-seat pricing, no admin console, no IT person needed."],
          ].map(([Icon, h, p]) => (
            <div key={h} className="border-t-2 border-slate-900 pt-5">
              <Icon size={20} className="dm-accent-text" />
              <h3 className="mt-4 font-head text-xl font-bold text-slate-900">{h}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{p}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* How it works */}
      <div id="how-it-works" className="scroll-mt-20 border-y border-slate-200 bg-white/60">
        <Section className="py-20 lg:py-28">
          <div className="max-w-2xl">
            <Heading>From paper to DentMemo in an afternoon.</Heading>
            <Lede>There is no migration project. Start with today's patients and add older ones as they come back.</Lede>
          </div>
          <ol className="mt-12 grid gap-8 md:grid-cols-4 md:gap-6">
            {[
              ["Create your clinic", "Name, address, your logo. Takes two minutes; you can change all of it later."],
              ["Invite your front desk", "Share the staff code. Your assistant signs up on their own phone or the desk computer."],
              ["Add patients as they arrive", "Register each patient the first time they visit — name, phone, and the basics. Search finds them from then on."],
              ["Log visits, settle consultants", "One line per visit. Settlements and the clinic overview build themselves from those lines."],
            ].map(([h, p], i) => (
              <li key={h} className="relative">
                <span className="font-mono text-sm font-semibold dm-accent-text">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-head text-lg font-bold text-slate-900">{h}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{p}</p>
              </li>
            ))}
          </ol>
        </Section>
      </div>

      {/* Pricing */}
      <div id="pricing" className="scroll-mt-20 border-y border-slate-200 bg-white/60">
        <Section className="pt-14 pb-16 lg:pt-16 lg:pb-20">
          <div className="max-w-2xl">
            <Heading>Simple pricing for your practice.</Heading>
            <Lede>Start free for 30 days. No credit card required.</Lede>
          </div>

          <div className="mt-8 grid gap-6 lg:grid-cols-3">
            {PLANS.map((p) => (
              <div
                key={p.key}
                className={`dm-panel flex h-full flex-col p-7 ${
                  p.emphasize
                    ? "border-2 border-[var(--dm-accent)] shadow-[0_28px_70px_-30px_rgba(37,99,235,0.35)] lg:-translate-y-3"
                    : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-head text-lg font-bold text-slate-900">{p.name}</h3>
                  {p.badge && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-teal-700">
                      <Sparkles size={11} /> {p.badge}
                    </span>
                  )}
                </div>

                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-head text-3xl font-extrabold tracking-tight text-slate-900">{p.price}</span>
                  {p.priceUnit && <span className="text-sm text-slate-500">{p.priceUnit}</span>}
                </div>

                <div className="mt-2 space-y-1 text-sm text-slate-500">
                  {p.lines.map((line, i) =>
                    typeof line === "string" ? (
                      <p key={i}>{line}</p>
                    ) : (
                      <p key={i}>
                        <s className="text-slate-400">{line.strike}</s>{" "}
                        <span className="font-semibold text-teal-700">{line.save}</span>
                      </p>
                    )
                  )}
                </div>

                <ul className="mt-6 flex-1 space-y-2.5 text-sm text-slate-600">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0 text-teal-600" />{f}</li>
                  ))}
                </ul>
                <button
                  onClick={() => (p.key === "trial" ? start() : choosePlan(p.cycle))}
                  className={`mt-7 w-full justify-center ${p.emphasize ? "dm-btn-primary" : "dm-btn-ghost"}`}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>

          <p className="mt-6 text-center text-sm text-slate-500">30 days free · No credit card · Cancel anytime</p>

          {/* Get started in 3 steps */}
          <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-head text-lg font-bold text-slate-900">Get started in 3 steps</h3>
            <div className="mt-5 grid gap-6 md:grid-cols-3 md:gap-6">
              {STEPS.map(([h, p], i) => (
                <div key={h} className="flex gap-3">
                  <span className="font-mono text-sm font-bold text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h4 className="font-head text-[15px] font-bold text-slate-900">{h}</h4>
                    <p className="mt-0.5 text-sm leading-relaxed text-slate-600">{p}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment trust */}
          <div className="mt-8 flex flex-wrap items-center gap-3 text-sm text-slate-500">
            <span className="flex items-center gap-1.5">
              <ShieldCheck size={15} className="shrink-0 text-teal-600" /> Secure payments via
            </span>
            <img src="/payment-logos/razorpay.svg" alt="Razorpay" className="h-4 w-auto object-contain" />
            <span className="text-slate-300">·</span>
            <div className="flex flex-wrap items-center gap-3.5 opacity-80">
              {PAYMENT_METHODS.map((m) => (
                <img key={m.alt} src={m.src} alt={m.alt} className="h-5 w-auto object-contain" />
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-10 max-w-3xl">
            <h3 className="font-head text-xl font-bold text-slate-900">Frequently asked questions</h3>
            <Accordion type="single" collapsible className="mt-4">
              {FAQS.map(([q, a]) => (
                <AccordionItem key={q} value={q}>
                  <AccordionTrigger className="font-head text-[15px] font-semibold text-slate-900">{q}</AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-slate-600">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Section>
      </div>

      {/* Social proof */}
      <Section id="clinics" className="scroll-mt-20 py-20 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <Heading>What dentists say</Heading>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {[
            ["\"My receptionist stopped asking me for the register. She just searches.\"", "Dr. Blessin Mathew", "Blessin’s Dental Studio, Aquem, Goa"],
            ["\"Settling consultants used to take me a Sunday. Now it is a screen.\"", "Dr. Miyola Fernandes", "Gold Dental Studio, Mapusa, Goa"],
          ].map(([q, n, c]) => (
            <figure key={c} className="dm-panel p-6">
              <blockquote className="font-head text-lg font-semibold leading-snug text-slate-800">{q}</blockquote>
              <figcaption className="mt-5 text-sm text-slate-500">
                <span className="block font-semibold text-slate-700">{n}</span>{c}
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section className="pb-20 lg:pb-28">
        <div className="relative overflow-hidden rounded-3xl bg-slate-900 px-6 py-14 text-white sm:px-12 lg:px-16">
          <div className="pointer-events-none absolute -right-10 -top-10 h-64 w-64 rounded-full bg-[#2563eb]/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 left-1/3 h-56 w-56 rounded-full bg-[#0d9488]/20 blur-3xl" />
          <div className="relative grid items-center gap-8 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <h2 className="font-head text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-4xl">Set up your clinic today. Log your first visit tomorrow morning.</h2>
              <p className="mt-4 max-w-lg text-slate-300">No card needed to start. Your data is yours — export or delete it any time from Settings.</p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
              <button onClick={start} className="dm-btn-primary !px-6 !py-3 text-[15px]">Set up your clinic <ArrowRight size={16} /></button>
              <button onClick={() => nav("/start", { state: { mode: "join" } })} className="dm-btn !px-6 !py-3 text-[15px] text-white ring-1 ring-inset ring-white/30 hover:bg-white/10">
                I have a staff code
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="border-t border-slate-200">
        <Section className="flex flex-col gap-6 py-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <Brand tagline />
            <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              {NAV.map(([l, h]) => <a key={h} href={h} className="hover:text-slate-900">{l}</a>)}
              <Link to="/login" className="hover:text-slate-900">Log in</Link>
              {LEGAL_LINKS.map(([l, h]) => <Link key={h} to={h} className="hover:text-slate-900">{l}</Link>)}
            </nav>
          </div>
          <CompanyInfo className="border-t border-slate-100 pt-6" />
        </Section>
      </footer>
    </div>
  );
}
